import React from "react";
import { useParams } from "react-router-dom";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { readAnimalCompleteByAnimalId } from "services/patients.service";

type Row = {
  id: string;
  address1: string;
  address2: string;
  town: string;
  city: string;
  zip: string;
};

function flattenAddressesFromAnimalComplete(contactList: unknown[]): Row[] {
  const out: Row[] = [];
  let n = 0;
  for (const c of contactList) {
    const contact = c as Record<string, unknown>;
    const addresses = contact.addressList ?? contact.AddressList;
    if (!Array.isArray(addresses)) continue;
    for (const a of addresses) {
      const addr = a as Record<string, unknown>;
      const id =
        addr.addressId != null || addr.AddressId != null
          ? String(addr.addressId ?? addr.AddressId)
          : `addr-${n++}`;
      out.push({
        id,
        address1: String(addr.address1 ?? addr.Address1 ?? ""),
        address2: String(addr.address2 ?? addr.Address2 ?? ""),
        town: String(addr.town ?? addr.Town ?? ""),
        city: String(addr.city ?? addr.City ?? ""),
        zip: String(addr.zipcode ?? addr.Zipcode ?? ""),
      });
    }
  }
  return out;
}

const AddressesList = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const animalId = id ? Number(id) : NaN;

  const [rows, setRows] = React.useState<Row[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await readAnimalCompleteByAnimalId(
        token,
        orgId,
        animalId
      );
      if (apiError) {
        setError(apiError.message);
        setRows([]);
        return;
      }
      setRows(flattenAddressesFromAnimalComplete(data?.contactList ?? []));
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId, animalId]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Client-side paging: the API returns the whole collection.
  const view = useTableView(rows);

  return (
    <div>
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="m-4"
      />

      <Table<Row>
        loading={loading}
        columns={{
          address1: "Address 1",
          address2: "Address 2",
          town: "Town",
          city: "City",
          zip: "Zipcode",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
      />

      {loading && (
        <div className="px-4 pb-2 text-sm text-gray-500">Loading addresses…</div>
      )}
      {error && <div className="px-4 pb-2 text-sm text-red-500">{error}</div>}

      <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />
    </div>
  );
};

export default AddressesList;
